import { Loading } from "../app/Status";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router";
import type { ArchivedBuilding, BuildingDto, BuildingSummary, ClientDto } from "@klantpunt/shared";
import { MoveDialog } from "../features/building/MoveDialog";
import { buildingsKey } from "../lib/buildingContext";
import { useClientBuildings, useClientContext } from "../lib/clientContext";
import { BuildingDialog, PermanentDelete } from "../features/building/dialogs";
import { buildingKey } from "../features/building/useBuilding";
import { api, ApiError } from "../lib/api";
import { can, useMe } from "../lib/auth";
import { useToast } from "../lib/toast";
import { IconChevronRight, IconPlus, IconSelect } from "../app/icons";
import { BuildingsMap } from "../features/map/BuildingsMap";
import { Tabs } from "../app/Choice";
import { PageHeader } from "../app/PageHeader";
import { OnboardingChecklist, useOnboarding, WelcomeDialog } from "../features/onboarding/Onboarding";

const VIEW_KEY = "pw-buildings-view";
const readView = (): "list" | "map" => {
  try {
    return localStorage.getItem(VIEW_KEY) === "map" ? "map" : "list";
  } catch {
    return "list";
  }
};

export function HomePage() {
  const { data: me } = useMe();
  const key = buildingsKey(me?.org?.id);
  const buildings = useClientBuildings();
  const { id: clientId, client, clients } = useClientContext();
  // Bij "Alle klanten" met meer klanten: gegroepeerd per klant.
  const groups = groupByClient(buildings.data ?? [], clientId ? [] : (clients ?? []));
  const [selecting, setSelecting] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [moving, setMoving] = useState(false);
  const toggle = (id: string) =>
    setSelected((cur) => {
      const next = new Set(cur);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const stopSelecting = () => {
    setSelecting(false);
    setSelected(new Set());
  };
  const [creating, setCreating] = useState(false);
  const [view, setViewRaw] = useState(readView);
  const setView = (v: "list" | "map") => {
    setViewRaw(v);
    try {
      localStorage.setItem(VIEW_KEY, v);
    } catch {
      /* geen opslag: alleen deze sessie */
    }
  };
  const qc = useQueryClient();
  const navigate = useNavigate();
  const toast = useToast();
  const canManage = can(me, "building.manage");
  const onboarding = useOnboarding();
  // Vanuit Aan de slag: /?nieuw=1 opent meteen "Nieuw gebouw".
  const [params, setParams] = useSearchParams();
  useEffect(() => {
    if (params.get("nieuw") !== "1") return;
    if (canManage) setCreating(true);
    setParams({}, { replace: true });
  }, [params, setParams, canManage]);
  const archiveKey = ["buildings", "archived", me?.org?.id];
  const archived = useQuery({
    queryKey: archiveKey,
    queryFn: () => api<ArchivedBuilding[]>("/buildings/archived"),
    enabled: canManage,
  });
  const refreshAll = () => qc.invalidateQueries({ queryKey: ["buildings"] });
  async function restore(b: ArchivedBuilding) {
    try {
      const nb = await api<BuildingDto>(`/buildings/${b.id}/restore`, { method: "POST" });
      qc.setQueryData(buildingKey(nb.id), nb);
      toast(`${b.name} teruggezet`);
      void refreshAll();
    } catch (e) {
      toast(e instanceof ApiError ? e.message : "Terugzetten mislukt");
    }
  }
  // Uitvoerders starten op "Mijn taken" (SPEC §8).
  if (me?.role === "uitvoerder") return <Navigate to="/taken" replace />;

  return (
    <div className="page">
      <PageHeader
        label="Gebouwen"
        title={client?.name ?? me?.org?.name}
        action={
          canManage && (
            <button type="button" className="btn primary" onClick={() => setCreating(true)}>
              <IconPlus size={20} aria-hidden="true" />
              Nieuw gebouw
            </button>
          )
        }
        tabs={
          buildings.data &&
          buildings.data.length > 0 && (
            <Tabs
              label="Weergave gebouwen"
              items={[
                { value: "list", label: "Lijst" },
                { value: "map", label: "Kaart" },
              ]}
              value={view}
              onChange={setView}
            />
          )
        }
      />
      {onboarding.data?.showChecklist && !onboarding.data.showWelcome && <OnboardingChecklist data={onboarding.data} />}
      <section className="panel" aria-busy={buildings.isPending}>
        {buildings.isPending ? (
          <Loading />
        ) : buildings.data && buildings.data.length > 0 && view === "map" ? (
          <BuildingsMap buildings={buildings.data} canManage={canManage} />
        ) : buildings.data && buildings.data.length > 0 ? (
          <>
            {canManage && !selecting && (
              <div className="listtools">
                <button type="button" className="btn ghost small" onClick={() => setSelecting(true)}>
                  <IconSelect size={18} aria-hidden="true" />
                  Selecteren en verplaatsen
                </button>
              </div>
            )}
            {selecting && (
              <div className="selectbar" role="region" aria-label="Selectie">
                <label className="small">
                  <input
                    type="checkbox"
                    checked={selected.size === buildings.data.length}
                    ref={(el) => {
                      if (el) el.indeterminate = selected.size > 0 && selected.size < buildings.data!.length;
                    }}
                    onChange={(e) =>
                      setSelected(e.target.checked ? new Set(buildings.data!.map((b) => b.id)) : new Set())
                    }
                  />{" "}
                  Alles
                </label>
                <span className="grow muted small">{selected.size} geselecteerd</span>
                <button
                  type="button"
                  className="btn primary small"
                  disabled={!selected.size}
                  onClick={() => setMoving(true)}
                >
                  Verplaatsen naar klant…
                </button>
                <button type="button" className="btn ghost small" onClick={stopSelecting}>
                  Klaar
                </button>
              </div>
            )}
            {groups.map((g) => (
              <section key={g.id ?? "all"} aria-label={g.name ?? undefined}>
                {g.name && (
                  <h2 className="label clientgroup">
                    {g.name} <span className="muted">({g.buildings.length})</span>
                  </h2>
                )}
                <ul className="list">
                  {g.buildings.map((b) =>
                    selecting ? (
                      <li key={b.id}>
                        <label className="buildinglink">
                          <input type="checkbox" checked={selected.has(b.id)} onChange={() => toggle(b.id)} />
                          <span className="code">{b.code}</span>
                          <span className="grow">
                            <strong>{b.name}</strong>
                            {b.address && <span className="muted addr">{b.address}</span>}
                          </span>
                        </label>
                      </li>
                    ) : (
                      <li key={b.id}>
                        <Link to={`/gebouwen/${b.id}`} className="buildinglink">
                          <span className="code">{b.code}</span>
                          <span className="grow">
                            <strong>{b.name}</strong>
                            {b.isDemo && <span className="chip demo">Voorbeeld</span>}
                            {b.address && <span className="muted addr">{b.address}</span>}
                          </span>
                          {(b.openCount ?? 0) > 0 && (
                            <span className={`chip ${(b.urgentCount ?? 0) > 0 ? "prio prio-P1" : ""}`}>
                              {b.openCount} open
                            </span>
                          )}
                          <IconChevronRight size={20} aria-hidden="true" className="muted" />
                        </Link>
                      </li>
                    ),
                  )}
                </ul>
              </section>
            ))}
          </>
        ) : (
          <p className="muted">
            {clientId ? "Deze klant heeft nog geen gebouwen." : "Nog geen gebouwen."}
            {canManage && " Maak er een aan met “Nieuw gebouw”."}
          </p>
        )}
      </section>
      {canManage && archived.data && archived.data.length > 0 && (
        <details className="panel archive">
          <summary>
            <strong>Gearchiveerd</strong> <span className="muted">({archived.data.length})</span>
          </summary>
          <ul className="list">
            {archived.data.map((b) => (
              <li key={b.id} className="archrow">
                <span className="code">{b.code}</span>
                <span className="grow">
                  <strong>{b.name}</strong>
                  <br />
                  <span className="muted">
                    Gearchiveerd op{" "}
                    {new Date(b.archivedAt).toLocaleDateString("nl-NL", {
                      day: "numeric",
                      month: "short",
                      year: "numeric",
                    })}
                  </span>
                </span>
                <span className="btnrow">
                  <button
                    type="button"
                    className="btn small"
                    aria-label={`${b.name} terugzetten`}
                    onClick={() => void restore(b)}
                  >
                    Terugzetten
                  </button>
                  <PermanentDelete
                    building={b}
                    detail="Het gebouw met alle verdiepingen, tekeningen, bevindingen, foto's en taken wordt gewist."
                    onDeleted={() => void refreshAll()}
                  />
                </span>
              </li>
            ))}
          </ul>
        </details>
      )}
      {onboarding.data?.showWelcome && me?.org && (
        <WelcomeDialog orgName={me.org.name} onNewBuilding={() => setCreating(true)} />
      )}
      {moving && buildings.data && (
        <MoveDialog
          buildings={buildings.data.filter((b) => selected.has(b.id))}
          onClose={() => setMoving(false)}
          onMoved={stopSelecting}
        />
      )}
      {creating && (
        <BuildingDialog
          onClose={() => setCreating(false)}
          onSaved={(b) => {
            qc.setQueryData(buildingKey(b.id), b);
            void qc.invalidateQueries({ queryKey: key });
            void qc.invalidateQueries({ queryKey: ["onboarding"] });
            setCreating(false);
            toast(`${b.name} toegevoegd`);
            navigate(`/gebouwen/${b.id}`);
          }}
        />
      )}
    </div>
  );
}

/** Gebouwen per klant, in de volgorde van de klantenlijst; zonder klantenlijst één groep zonder kop. */
function groupByClient(list: BuildingSummary[], clients: ClientDto[]) {
  const withBuildings = clients.filter((c) => list.some((b) => b.clientId === c.id));
  if (withBuildings.length < 2) return [{ id: null, name: null, buildings: list }];
  return withBuildings.map((c) => ({ id: c.id, name: c.name, buildings: list.filter((b) => b.clientId === c.id) }));
}
